import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import {
  ArrowLeft,
  Camera,
  Check,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Lock,
  Phone,
  Bot,
  Shield,
  User,
  Upload,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { authApi, botsApi, usersApi } from '../../../api';
import { useUIStore } from '../../../store';
import { FILE_TYPES } from '../../../utils/constants';
import { resolveUserAvatar } from '../../../utils/media';

const ProfilePage = ({ session, sess }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useUIStore();
  const user = session || sess || {};
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileForm, setProfileForm] = useState({
    displayName: user.u_name || user.name || '',
    phone: user.u_mobile || user.phone || '',
    country: user.u_country || user.country || '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [avatarUrl, setAvatarUrl] = useState(user.u_image || user.image || '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [bots, setBots] = useState([]);
  const [primaryBotId, setPrimaryBotId] = useState(Number(user.p_bot || 0));

  const passwordStrength = useMemo(() => {
    const pwd = passwordForm.newPassword;
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 6) strength += 25;
    if (pwd.length >= 8) strength += 25;
    if (/[A-Z]/.test(pwd)) strength += 25;
    if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) strength += 25;
    return strength;
  }, [passwordForm.newPassword]);

  const getStrengthColor = (strength) => {
    if (strength <= 25) return '#ef4444';
    if (strength <= 50) return '#f59e0b';
    if (strength <= 75) return '#3b82f6';
    return '#10b981';
  };

  const getStrengthLabel = (strength) => {
    if (strength <= 25) return 'Weak';
    if (strength <= 50) return 'Fair';
    if (strength <= 75) return 'Good';
    return 'Strong';
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'phone') {
      nextValue = String(value).replace(/\D+/g, '');
    }
    if (name === 'displayName') {
      nextValue = String(value).replace(/[^a-zA-Z\s]/g, '');
    }
    setProfileForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => {
      if (name === 'country') {
        return { ...prev, country: undefined, phone: undefined };
      }
      return { ...prev, [name]: undefined };
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const resolvedAvatarUrl = useMemo(() => resolveUserAvatar(avatarUrl), [avatarUrl]);

  useEffect(() => {
    setProfileForm({
      displayName: user.u_name || user.name || '',
      phone: user.u_mobile || user.phone || '',
      country: user.u_country || user.country || '',
    });
    setAvatarUrl(user.u_image || user.image || '');
    setPrimaryBotId(Number(user.p_bot || 0));
  }, [user?.uid]);

  useEffect(() => {
    const loadWorkspaceOptions = async () => {
      try {
        const [countriesResponse, botsResponse] = await Promise.all([
          usersApi.getCountries({ active: 1 }),
          botsApi.getActive(),
        ]);
        const countriesPayload =
          countriesResponse?.data?.data || countriesResponse?.data || [];
        setCountries(Array.isArray(countriesPayload) ? countriesPayload : []);
        const botsPayload = botsResponse?.data?.data || botsResponse?.data || [];
        setBots(Array.isArray(botsPayload) ? botsPayload : []);
      } catch (error) {
        console.error('Failed to load workspace options', error);
        showError('Failed to load countries or bots');
      }
    };

    loadWorkspaceOptions();
  }, [showError]);

  const validateProfile = () => {
    const nextErrors = {};
    const nameValue = String(profileForm.displayName || '').trim();
    if (!nameValue || nameValue.length < 2) {
      nextErrors.displayName = 'Name must be at least 2 characters';
    }
    const phoneValue = String(profileForm.phone || '').trim();
    if (phoneValue) {
      const countryValue = String(profileForm.country || '').trim();
      const selectedCountry = countries.find((country) =>
        String(country.c_id ?? country.id ?? country.value ?? '') === countryValue
      );
      const countryLabel =
        selectedCountry?.countryName ||
        selectedCountry?.country_name ||
        selectedCountry?.name ||
        countryValue;
      const rawIso =
        selectedCountry?.iso2 ||
        selectedCountry?.country_iso2 ||
        selectedCountry?.countryIso2 ||
        selectedCountry?.countryCode ||
        selectedCountry?.country_code ||
        selectedCountry?.c_code ||
        selectedCountry?.code ||
        selectedCountry?.alpha2;
      const iso2 = rawIso ? String(rawIso).toUpperCase() : '';
      const normalizedCountry = String(countryValue).toUpperCase();
      const resolvedIso2 =
        iso2 ||
        (normalizedCountry === 'IND' || normalizedCountry === 'IN' || String(countryLabel).toLowerCase() === 'india'
          ? 'IN'
          : '');
      if (resolvedIso2) {
        const parsed = parsePhoneNumberFromString(phoneValue, resolvedIso2);
        if (!parsed || !parsed.isValid()) {
          nextErrors.phone = `Enter a valid ${countryLabel || 'phone'} number`;
        }
      } else if (!/^\d{6,15}$/.test(phoneValue)) {
        nextErrors.phone = 'Enter a valid phone number';
      }
    }
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const validatePassword = () => {
    const nextErrors = {};
    if (!passwordForm.currentPassword) {
      nextErrors.currentPassword = 'Current password is required';
    }
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
      nextErrors.newPassword = 'New password must be at least 6 characters';
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;
    setSavingProfile(true);
    try {
      const payload = {};
      if (profileForm.displayName) payload.name = profileForm.displayName.trim();
      if (profileForm.phone) payload.mobile = profileForm.phone.trim();
      if (profileForm.country) payload.country = profileForm.country;
      await authApi.updateProfile(payload);
      showSuccess('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile', error);
      showError(error?.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;
    setSavingPassword(true);
    try {
      await authApi.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      showSuccess('Password updated successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Failed to change password', error);
      showError(error?.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!FILE_TYPES.IMAGE.includes(file.type)) {
      showError('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showError('Image size must be under 2MB');
      return;
    }
    const formData = new FormData();
    formData.append('image', file);
    setUploadingImage(true);
    try {
      const response = await authApi.uploadProfileImage(formData);
      const imagePath =
        response?.data?.imageUrl ||
        response?.data?.data?.imageUrl ||
        response?.data?.data?.image_path;
      if (imagePath) {
        setAvatarUrl(imagePath);
        try {
          const storedSession = localStorage.getItem('session');
          if (storedSession) {
            const parsed = JSON.parse(storedSession);
            const nextSession = { ...parsed, u_image: imagePath, image: imagePath };
            localStorage.setItem('session', JSON.stringify(nextSession));
            window.dispatchEvent(new CustomEvent('session:updated', { detail: { session: nextSession } }));
          }
        } catch (error) {
          console.error('Failed to update stored session image:', error);
        }
      }
      showSuccess('Profile image updated');
    } catch (error) {
      console.error('Failed to upload image', error);
      showError(error?.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleWorkspaceSubmit = async (event) => {
    event.preventDefault();
    setSavingWorkspace(true);
    try {
      const nextBotId = primaryBotId || 0;
      await usersApi.updatePrimaryBot({ p_bot: nextBotId, botId: nextBotId });
      showSuccess('Primary bot updated');
    } catch (error) {
      console.error('Failed to update primary bot', error);
      showError(error?.response?.data?.error || 'Failed to update primary bot');
    } finally {
      setSavingWorkspace(false);
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#94a3b8',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#6366f1',
        borderWidth: 2,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#6366f1',
    },
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: '#f8fafc',
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              '&:hover': { backgroundColor: '#f1f5f9' },
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Profile Settings
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Manage your account settings and preferences
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {/* Profile Photo Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      src={resolvedAvatarUrl}
                      alt={user.u_name || user.name || 'User'}
                      sx={{
                        width: 120,
                        height: 120,
                        border: '4px solid #fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    />
                    <IconButton
                      component="label"
                      disabled={uploadingImage}
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        backgroundColor: '#6366f1',
                        color: '#fff',
                        width: 36,
                        height: 36,
                        '&:hover': { backgroundColor: '#4f46e5' },
                        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.4)',
                      }}
                    >
                      <Camera size={18} />
                      <input hidden type="file" accept="image/*" onChange={handleImageUpload} />
                    </IconButton>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                      {profileForm.displayName || user.name || 'User'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                      {user.u_email || user.email || '-'}
                    </Typography>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      disabled={uploadingImage}
                      startIcon={<Upload size={16} />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        borderColor: '#e2e8f0',
                        color: '#475569',
                        '&:hover': {
                          borderColor: '#6366f1',
                          backgroundColor: alpha('#6366f1', 0.04),
                        },
                      }}
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload Photo'}
                      <input hidden type="file" accept="image/*" onChange={handleImageUpload} />
                    </Button>
                    <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 1 }}>
                      JPG, PNG or GIF. Max 2MB.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Personal Information */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: alpha('#6366f1', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <User size={20} color="#6366f1" />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Personal Information
                  </Typography>
                </Stack>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box component="form" onSubmit={handleProfileSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <TextField
                        label="Full Name"
                        name="displayName"
                        value={profileForm.displayName}
                        onChange={handleProfileChange}
                        error={Boolean(errors.displayName)}
                        helperText={errors.displayName}
                        fullWidth
                        placeholder="Enter your full name"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <User size={18} color="#94a3b8" />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Phone Number"
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleProfileChange}
                        error={Boolean(errors.phone)}
                        helperText={errors.phone}
                        fullWidth
                        placeholder="Enter phone number"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone size={18} color="#94a3b8" />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ '&.Mui-focused': { color: '#6366f1' } }}>
                          Country
                        </InputLabel>
                        <Select
                          label="Country"
                          name="country"
                          value={profileForm.country || ''}
                          onChange={handleProfileChange}
                          startAdornment={
                            <InputAdornment position="start">
                              <Globe size={18} color="#94a3b8" />
                            </InputAdornment>
                          }
                          sx={{
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#94a3b8',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6366f1',
                              borderWidth: 2,
                            },
                          }}
                        >
                          {countries.length === 0 && (
                            <MenuItem disabled value="">
                              No countries available
                            </MenuItem>
                          )}
                          {countries.map((country) => (
                            <MenuItem key={country.c_id} value={country.c_id}>
                              {country.countryName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={savingProfile}
                      startIcon={<Check size={18} />}
                      sx={{
                        backgroundColor: '#6366f1',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
                        '&:hover': {
                          backgroundColor: '#4f46e5',
                        },
                      }}
                    >
                      {savingProfile ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Change Password */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: alpha('#f59e0b', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Shield size={20} color="#f59e0b" />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Change Password
                  </Typography>
                </Stack>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box component="form" onSubmit={handlePasswordSubmit}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <TextField
                        label="Current Password"
                        name="currentPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        error={Boolean(errors.currentPassword)}
                        helperText={errors.currentPassword}
                        fullWidth
                        placeholder="Enter current password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock size={18} color="#94a3b8" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                                {showPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="New Password"
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        error={Boolean(errors.newPassword)}
                        helperText={errors.newPassword}
                        fullWidth
                        placeholder="Enter new password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <KeyRound size={18} color="#94a3b8" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowNewPassword((p) => !p)} edge="end" size="small">
                                {showNewPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword}
                        fullWidth
                        placeholder="Confirm new password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <KeyRound size={18} color="#94a3b8" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowConfirmPassword((p) => !p)} edge="end" size="small">
                                {showConfirmPassword ? <EyeOff size={18} color="#94a3b8" /> : <Eye size={18} color="#94a3b8" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={inputStyles}
                      />
                    </Grid>

                    {/* Password Strength */}
                    {passwordForm.newPassword && (
                      <Grid item xs={12}>
                        <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 2, p: 2 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                              Password Strength
                            </Typography>
                            <Chip
                              label={getStrengthLabel(passwordStrength)}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                backgroundColor: alpha(getStrengthColor(passwordStrength), 0.1),
                                color: getStrengthColor(passwordStrength),
                              }}
                            />
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={passwordStrength}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: '#e2e8f0',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: getStrengthColor(passwordStrength),
                              },
                            }}
                          />
                          <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                            {[
                              { label: '6+ characters', met: passwordForm.newPassword.length >= 6 },
                              { label: 'Uppercase', met: /[A-Z]/.test(passwordForm.newPassword) },
                              { label: 'Number', met: /[0-9]/.test(passwordForm.newPassword) },
                            ].map((item) => (
                              <Typography
                                key={item.label}
                                variant="caption"
                                sx={{
                                  color: item.met ? '#10b981' : '#94a3b8',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {item.met ? <Check size={12} /> : '○'} {item.label}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={savingPassword}
                      startIcon={<Shield size={18} />}
                      sx={{
                        backgroundColor: '#f59e0b',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.25)',
                        '&:hover': {
                          backgroundColor: '#d97706',
                        },
                      }}
                    >
                      {savingPassword ? 'Updating...' : 'Update Password'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Primary Bot */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: alpha('#10b981', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Bot size={20} color="#10b981" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      Default Bot
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Select your primary bot for new conversations
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box component="form" onSubmit={handleWorkspaceSubmit}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ '&.Mui-focused': { color: '#10b981' } }}>
                      Primary Bot
                    </InputLabel>
                    <Select
                      label="Primary Bot"
                      value={primaryBotId || 0}
                      onChange={(event) => setPrimaryBotId(Number(event.target.value))}
                      startAdornment={
                        <InputAdornment position="start">
                          <Bot size={18} color="#94a3b8" />
                        </InputAdornment>
                      }
                      sx={{
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#94a3b8',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#10b981',
                          borderWidth: 2,
                        },
                      }}
                    >
                      <MenuItem value={0}>None</MenuItem>
                      {bots.map((bot) => (
                        <MenuItem key={bot.bid || bot.b_id || bot.id} value={Number(bot.bid || bot.b_id || bot.id)}>
                          {bot.b_name || bot.name || `Bot ${bot.bid || bot.b_id || bot.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={savingWorkspace}
                      startIcon={savingWorkspace ? <CircularProgress size={16} color="inherit" /> : <Check size={18} />}
                      sx={{
                        backgroundColor: '#10b981',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                        '&:hover': {
                          backgroundColor: '#059669',
                        },
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfilePage;
